import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { OutlinedBadge } from '@tms/shared-ui';
import TeachersLocationMap from '../components/teachers/TeachersLocationMap';
import { getTeachersSummary, type TeachersSummary } from '../services/adminAnalyticsApi';
import { useLanguage } from '../hooks/useLanguage';
import { ContentPageLayout } from '../components/Shared';

const MetricCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%' }}>
    <Typography variant="h5" color="primary" fontWeight="bold">
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Paper>
);

const TeachersDashboardPage: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TeachersSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTeachersSummary();
      setData(res.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load teachers data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const k = data?.kpis;

  return (
    <ContentPageLayout>
    <Box>
      <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
        Teachers
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Workload, student engagement, and localization.
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : k ? (
        <>
          <Grid container spacing={2} mb={3} sx={{ mt: 1 }}>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Teachers" value={k.totalTeachers} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Assigned students" value={k.totalAssignedStudents} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Avg students" value={k.averageStudentsPerTeacher} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="No students" value={k.teachersWithZeroStudents} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Overloaded" value={k.overloadedTeachers} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Students w/o teacher" value={k.studentsWithoutTeacher} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Teacher workload
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Teacher</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell align="right">Students</TableCell>
                      <TableCell align="right">Active 30d</TableCell>
                      <TableCell align="right">Sessions (mo)</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.teachers || []).map((t) => (
                      <TableRow key={t.teacherId}>
                        <TableCell>
                          <Link to={`/${currentLanguage}/teachers/${t.teacherId}`}>
                            {t.nickname || t.email || t.teacherId}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {[t.city, t.postalCode].filter(Boolean).join(' ') || '—'}
                        </TableCell>
                        <TableCell align="right">{t.studentCount}</TableCell>
                        <TableCell align="right">{t.activeStudents30d}</TableCell>
                        <TableCell align="right">{t.sessionsThisMonth}</TableCell>
                        <TableCell>
                          {t.overloaded ? (
                            <OutlinedBadge label="Overloaded" color="error" size="small" />
                          ) : t.studentCount === 0 ? (
                            <OutlinedBadge label="No students" size="small" />
                          ) : t.atRiskStudents > 0 ? (
                            <OutlinedBadge
                              label={`${t.atRiskStudents} inactive`}
                              color="warning"
                              size="small"
                            />
                          ) : (
                            <OutlinedBadge label="OK" color="success" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Localization map
                </Typography>
                <TeachersLocationMap points={data?.mapPoints || []} />
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Students by city
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>City</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.studentsByLocation || []).map((loc) => (
                      <TableRow key={`${loc.city}-${loc.postalCode}`}>
                        <TableCell>
                          {loc.city}
                          {loc.postalCode ? ` (${loc.postalCode})` : ''}
                        </TableCell>
                        <TableCell align="right">{loc.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : null}
    </Box>
    </ContentPageLayout>
  );
};

export default TeachersDashboardPage;
